import type { Broadcast } from '~/pa-services.server'
import path from 'path-browserify'
import React from 'react'
import { useStableAccessor } from '~/hooks/use-stable-accessor'

export function useBroadcast<Event extends Broadcast.EventName>(
  eventName: Event | null,
  onEvent: NoInfer<Broadcast.EventHandler<Event>> = () => {}
): Broadcast.EventData<Event> | null {
  const getOnEvent = useStableAccessor(onEvent)
  const [lastEvent, setLastEvent] = React.useState<Broadcast.EventData<Event> | null>(null)

  React.useEffect(() => {
    if (!eventName) return
    const abortController = new AbortController()
    listenToBroadcast(
      eventName,
      (message) => {
        setLastEvent(message.data)
        getOnEvent()(message)
      },
      abortController.signal
    )
    return () => abortController.abort()
  }, [eventName, getOnEvent])

  return lastEvent
}

/**
 * Every listener of an event shares one EventSource, which closes once the last listener aborts.
 */
export function listenToBroadcast<Event extends Broadcast.EventName>(
  eventName: Event,
  onEvent: Broadcast.EventHandler<Event>,
  signal: AbortSignal
): void {
  if (signal.aborted) return
  const connection = getOrOpenBroadcastConnection(eventName)
  const handler: BroadcastMessageHandler = (message) => (onEvent as BroadcastMessageHandler)(message)
  connection.handlers.add(handler)
  signal.addEventListener(
    'abort',
    () => {
      connection.handlers.delete(handler)
      if (connection.handlers.size > 0 || broadcastConnections.get(eventName) !== connection) return
      closeBroadcastConnection(connection)
      broadcastConnections.delete(eventName)
    },
    { once: true }
  )
}

/**
 * Returns a eventName with params interpolated.
 *
 * @category Utils
 */
export const generateBroadcastEventName = ((originalPattern, params = {} as any) => {
  let pattern: string = originalPattern

  // ensure `/` is added at the beginning if the event name is absolute
  const prefix = pattern.startsWith('/') ? '/' : ''

  const stringify = (p: any) => (p == null ? '' : typeof p === 'string' ? p : String(p))

  const segments = pattern
    .split(/\/+/)
    .map((segment, index, array) => {
      const isLastSegment = index === array.length - 1

      // only apply the splat if it's the last segment
      if (isLastSegment && segment === '*') {
        const star = '*'
        // Apply the splat
        return stringify(params[star])
      }

      const keyMatch = segment.match(/^:([\w-]+)(\??)$/)
      if (keyMatch) {
        const [, key] = keyMatch
        let param = params[key]
        return stringify(param)
      }

      // Remove any optional markers from optional static segments
      return segment.replace(/\?$/g, '')
    })
    // Remove empty segments
    .filter((segment) => !!segment)

  return prefix + segments.join('/')
}) as (typeof Broadcast)['generateEventName']

type BroadcastMessage = { data: any; eventName: string; params: Record<string, string> }
type BroadcastMessageHandler = (message: BroadcastMessage) => void

type BroadcastConnection = {
  eventName: string
  eventSource: EventSource
  handlers: Set<BroadcastMessageHandler>
  consecutiveFailedConnections: number
  reconnectTimeout: ReturnType<typeof setTimeout> | null
}

const broadcastConnections = new Map<string, BroadcastConnection>()

function getOrOpenBroadcastConnection(eventName: string): BroadcastConnection {
  const existingConnection = broadcastConnections.get(eventName)
  if (existingConnection != null) return existingConnection
  const openedConnection: BroadcastConnection = {
    eventName,
    eventSource: openBroadcastEventSource(eventName),
    handlers: new Set(),
    consecutiveFailedConnections: 0,
    reconnectTimeout: null
  }
  attachBroadcastEventSourceListeners(openedConnection)
  broadcastConnections.set(eventName, openedConnection)
  return openedConnection
}

function closeBroadcastConnection(connection: BroadcastConnection): void {
  if (connection.reconnectTimeout != null) clearTimeout(connection.reconnectTimeout)
  connection.eventSource.close()
}

/**
 * EventSource reconnects on its own after a dropped stream. It gives up only on a fatal response, such as a 403,
 * which is the one case reopened here, with backoff.
 */
function attachBroadcastEventSourceListeners(connection: BroadcastConnection): void {
  const { eventSource } = connection
  eventSource.addEventListener('message', (event) => {
    const message = JSON.parse(event.data) as BroadcastMessage
    if (message.eventName === 'ping') return
    connection.handlers.forEach((handler) => {
      try {
        handler(message)
      } catch (error) {
        queueMicrotask(() => {
          throw error
        })
      }
    })
  })
  eventSource.addEventListener('open', () => {
    connection.consecutiveFailedConnections = 0
  })
  eventSource.addEventListener('error', (error) => {
    console.error(error)
    if (eventSource.readyState !== EventSource.CLOSED) return
    const reconnectDelayMilliseconds = Math.min(
      Math.pow(2, connection.consecutiveFailedConnections) * 1_000,
      MAXIMUM_BROADCAST_RECONNECT_DELAY_MILLISECONDS
    )
    connection.consecutiveFailedConnections++
    connection.reconnectTimeout = setTimeout(() => {
      connection.reconnectTimeout = null
      connection.eventSource = openBroadcastEventSource(connection.eventName)
      attachBroadcastEventSourceListeners(connection)
    }, reconnectDelayMilliseconds)
  })
}

function openBroadcastEventSource(eventName: string): EventSource {
  return new EventSource(path.join('/api-v2/broadcast', eventName), { withCredentials: true })
}

const MAXIMUM_BROADCAST_RECONNECT_DELAY_MILLISECONDS = 60_000
